import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import PoolIcon from '@mui/icons-material/Pool';
import EventIcon from '@mui/icons-material/Event';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import swimService from '../../api/services/swimService';
import {
  MyCalendarEntry,
  SwimCompetitionRow,
  SwimEnrollment,
  HouseholdSwimmerAttendance,
} from '../../types/swim';

const EVENT_CAP_PER_MEET = 3;

interface MeetSummary {
  competition: SwimCompetitionRow;
  swimmers: HouseholdSwimmerAttendance[];
}

interface SwimmerRow {
  swimmer_label: string;
  enrollments: SwimEnrollment[];
  next_session: MyCalendarEntry | null;
}

const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatSessionWhen = (entry: MyCalendarEntry): string => {
  if (!entry.session_date) return '';
  const [y, m, d] = entry.session_date.split('-').map(Number);
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  const dayLabel = dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  if (entry.start_time) {
    const [hh, mm] = entry.start_time.split(':').map(Number);
    const ampm = hh >= 12 ? 'PM' : 'AM';
    const hr = hh % 12 || 12;
    return `${dayLabel} • ${hr}:${String(mm ?? 0).padStart(2, '0')} ${ampm}`;
  }
  return dayLabel;
};

const formatMeetDate = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const SwimMyFamilyCard: React.FC = () => {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState<SwimEnrollment[]>([]);
  const [entries, setEntries] = useState<MyCalendarEntry[]>([]);
  const [meets, setMeets] = useState<MeetSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const [rows, cal] = await Promise.all([
          swimService.myEnrollments(),
          swimService.myCalendar(),
        ]);
        if (cancelled) return;

        setEnrollments(rows.filter((r) => r.status === 'registered' || r.status === 'waitlisted'));
        setEntries(cal.entries);

        const today = todayIso();
        const upcoming = cal.competitions
          .filter((c) => c.competition_date >= today)
          .sort((a, b) => a.competition_date.localeCompare(b.competition_date))
          .slice(0, 2);

        const summaries = await Promise.all(
          upcoming.map(async (comp): Promise<MeetSummary | null> => {
            try {
              const att = await swimService.getCompetitionAttendance(comp.id);
              return { competition: att.competition, swimmers: att.swimmers };
            } catch {
              return null;
            }
          }),
        );
        if (cancelled) return;
        setMeets(summaries.filter((s): s is MeetSummary => s !== null));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const swimmerRows: SwimmerRow[] = useMemo(() => {
    const today = todayIso();
    const byKey = new Map<string, SwimmerRow>();

    for (const enr of enrollments) {
      const first = enr.enrollee?.first_name ?? '';
      const last = enr.enrollee?.last_name ?? '';
      const key = enr.enrollee ? `${enr.enrollee.type}:${enr.enrollee.id}` : `unknown:${enr.id}`;
      const label = `${first} ${last}`.trim() || 'Swimmer';

      const existing = byKey.get(key);
      if (existing) {
        existing.enrollments.push(enr);
      } else {
        byKey.set(key, { swimmer_label: label, enrollments: [enr], next_session: null });
      }
    }

    for (const row of Array.from(byKey.values())) {
      const enrollmentIds = new Set<string>(row.enrollments.map((e: SwimEnrollment) => e.id));
      const futureSessions = entries
        .filter((entry) => enrollmentIds.has(entry.enrollment_id))
        .filter((entry) => entry.session_date && entry.session_date >= today)
        .filter((entry) => !entry.is_cancelled && entry.rsvp_status !== 'skip')
        .sort((a, b) => {
          const dateCmp = (a.session_date ?? '').localeCompare(b.session_date ?? '');
          if (dateCmp !== 0) return dateCmp;
          return (a.start_time ?? '').localeCompare(b.start_time ?? '');
        });
      row.next_session = futureSessions[0] ?? null;
    }

    return Array.from(byKey.values()).sort((a, b) => a.swimmer_label.localeCompare(b.swimmer_label));
  }, [enrollments, entries]);

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PoolIcon color="primary" />
          Swim — My Family
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : swimmerRows.length === 0 ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No active swim enrollments yet. Browse classes to sign up.
            </Typography>
            <Button variant="contained" fullWidth onClick={() => navigate('/swim')}>
              Browse Swim Classes
            </Button>
          </>
        ) : (
          <Stack spacing={1.5}>
            {swimmerRows.map((row) => {
              const programNames = Array.from(
                new Set(row.enrollments.map((e) => e.program?.name).filter((n): n is string => Boolean(n))),
              );
              const waitlisted = row.enrollments.some((e) => e.status === 'waitlisted');
              return (
                <Box key={row.swimmer_label}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="baseline"
                    sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {row.swimmer_label}
                    </Typography>
                    {programNames.map((name) => (
                      <Typography key={name} variant="caption" color="text.secondary">
                        {name}
                      </Typography>
                    ))}
                    {waitlisted && <Chip size="small" color="warning" label="waitlisted" />}
                  </Stack>
                  {row.next_session ? (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                      <EventIcon fontSize="inherit" sx={{ color: 'text.secondary', fontSize: 14 }} />
                      <Typography variant="caption" color="text.secondary">
                        Next: {formatSessionWhen(row.next_session)}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No upcoming sessions
                    </Typography>
                  )}
                </Box>
              );
            })}

            {meets.length > 0 && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Box>
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                    <EmojiEventsIcon fontSize="inherit" sx={{ color: 'warning.main', fontSize: 16 }} />
                    <Typography variant="overline" sx={{ lineHeight: 1 }}>
                      Upcoming Meets
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    {meets.map(({ competition, swimmers }) => {
                      const attending = swimmers.filter((s) => s.attending);
                      return (
                        <Box key={competition.id}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatMeetDate(competition.competition_date)} — {competition.name}
                          </Typography>
                          {attending.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              No swimmer RSVP'd yet
                            </Typography>
                          ) : (
                            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.25 }}>
                              {attending.map((s) => (
                                <Chip
                                  key={s.member_id}
                                  size="small"
                                  variant="outlined"
                                  label={`${s.first_name}: ${s.event_codes.length}/${EVENT_CAP_PER_MEET}`}
                                  color={s.event_codes.length === 0 ? 'default' : 'success'}
                                />
                              ))}
                            </Stack>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </>
            )}

            <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
              <Button
                variant="outlined"
                size="small"
                endIcon={<ChevronRightIcon />}
                onClick={() => navigate('/swim/schedule')}
                fullWidth
              >
                Schedule
              </Button>
              <Button
                variant="outlined"
                size="small"
                endIcon={<ChevronRightIcon />}
                onClick={() => navigate('/swim/my')}
                fullWidth
              >
                Enrollments
              </Button>
            </Stack>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default SwimMyFamilyCard;
